// Validation and Sanitization Utilities

// Sanitize string input (remove HTML tags, trim whitespace)
export const sanitizeString = (str) => {
  if (!str || typeof str !== 'string') return '';
  return (
    // Remove dangerous characters
    // Remove HTML tags
    str?.replace(/<[^>]*>/g, '')?.replace(/[<>"']/g, '')?.trim()
  );
};

// Sanitize number input
export const sanitizeNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const num = parseFloat(value);
  return isNaN(num) ? null : num;
};

// Sanitize email
export const sanitizeEmail = (email) => {
  if (!email || typeof email !== 'string') return '';
  return email?.toLowerCase()?.trim();
};

// Validate email format - comprehensive regex
export const validateEmail = (email) => {
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
  return emailRegex?.test(email);
};

// Validate email with error message
export const validateEmailField = (email, fieldName = 'Email') => {
  if (!email || email?.trim() === '') return `${fieldName} is required`;
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
  if (!emailRegex?.test(email?.trim())) {
    return `${fieldName} must be a valid email address (e.g., user@example.com)`;
  }
  return null;
};

// Validate required field
export const validateRequired = (value, fieldName = 'This field') => {
  if (value === null || value === undefined || value === '') {
    return `${fieldName} is required`;
  }
  if (typeof value === 'string' && value?.trim() === '') {
    return `${fieldName} is required`;
  }
  return null;
};

// Validate minimum length
export const validateMinLength = (value, minLength, fieldName = 'This field') => {
  if (!value || typeof value !== 'string') return null;
  if (value?.length < minLength) {
    return `${fieldName} must be at least ${minLength} characters`;
  }
  return null;
};

// Validate maximum length
export const validateMaxLength = (value, maxLength, fieldName = 'This field') => {
  if (!value || typeof value !== 'string') return null;
  if (value?.length > maxLength) {
    return `${fieldName} must not exceed ${maxLength} characters`;
  }
  return null;
};

// Validate number range
export const validateNumberRange = (value, min, max, fieldName = 'This field') => {
  const num = parseFloat(value);
  if (isNaN(num)) {
    return `${fieldName} must be a valid number`;
  }
  if (min !== null && num < min) {
    return `${fieldName} must be at least ${min}`;
  }
  if (max !== null && num > max) {
    return `${fieldName} must not exceed ${max}`;
  }
  return null;
};

// Validate positive number (no negatives)
export const validatePositiveNumber = (value, fieldName = 'Amount') => {
  const num = parseFloat(value);
  if (isNaN(num)) {
    return `${fieldName} must be a valid number`;
  }
  if (num < 0) {
    return `${fieldName} cannot be negative`;
  }
  if (num === 0) {
    return `${fieldName} must be greater than 0`;
  }
  return null;
};

// Validate non-negative amount (>= 0)
export const validateNonNegativeAmount = (value, fieldName = 'Amount') => {
  if (value === null || value === undefined || value === '') {
    return `${fieldName} is required`;
  }
  const num = parseFloat(value);
  if (isNaN(num)) {
    return `${fieldName} must be a valid number`;
  }
  if (num < 0) {
    return `${fieldName} cannot be a negative value`;
  }
  return null;
};

// Validate amount > 0 (strictly positive, no negatives)
export const validateAmount = (value, fieldName = 'Amount') => {
  if (value === null || value === undefined || value === '') {
    return `${fieldName} is required`;
  }
  const num = parseFloat(value);
  if (isNaN(num)) {
    return `${fieldName} must be a valid number`;
  }
  if (num < 0) {
    return `${fieldName} cannot be a negative value`;
  }
  if (num === 0) {
    return `${fieldName} must be greater than $0.00`;
  }
  return null;
};

// Validate date
export const validateDate = (dateString, fieldName = 'Date') => {
  if (!dateString) {
    return `${fieldName} is required`;
  }
  const date = new Date(dateString);
  if (isNaN(date?.getTime())) {
    return `${fieldName} must be a valid date`;
  }
  return null;
};

// Validate date not in future (for transaction dates, payment history, receipts)
export const validateDateNotFuture = (dateString, fieldName = 'Date') => {
  const dateError = validateDate(dateString, fieldName);
  if (dateError) return dateError;
  
  const date = new Date(dateString);
  const today = new Date();
  today?.setHours(23, 59, 59, 999);
  
  if (date > today) {
    return `${fieldName} cannot be a future date`;
  }
  return null;
};

// Validate date must be in future (for bill due dates, goal target dates, retirement dates)
export const validateDateMustBeFuture = (dateString, fieldName = 'Date') => {
  const dateError = validateDate(dateString, fieldName);
  if (dateError) return dateError;
  
  const date = new Date(dateString);
  const today = new Date();
  today?.setHours(0, 0, 0, 0);
  
  if (date < today) {
    return `${fieldName} must be a future date`;
  }
  return null;
};

// Validate phone number (Canadian format)
export const validatePhoneNumber = (phone) => {
  if (!phone) return null;
  const phoneRegex = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/;
  if (!phoneRegex?.test(phone)) {
    return 'Please enter a valid phone number';
  }
  return null;
};

// Validate postal code (Canadian format)
export const validatePostalCode = (postalCode) => {
  if (!postalCode) return null;
  const postalCodeRegex = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
  if (!postalCodeRegex?.test(postalCode)) {
    return 'Please enter a valid Canadian postal code (e.g., A1A 1A1)';
  }
  return null;
};

/**
 * Check for duplicate transactions within a 1-hour window
 * @param {Object} newTransaction - {description, amount, date}
 * @param {Array} existingTransactions - array of existing transactions
 * @returns {Object|null} duplicate transaction if found, null otherwise
 */
export const checkDuplicateTransaction = (newTransaction, existingTransactions) => {
  if (!existingTransactions?.length) return null;
  
  const newDate = new Date(newTransaction?.date || new Date());
  const oneHourMs = 60 * 60 * 1000;
  
  return existingTransactions?.find(existing => {
    const existingDate = new Date(existing?.date || existing?.created_at);
    const timeDiff = Math.abs(newDate - existingDate);
    const sameAmount = Math.abs(parseFloat(existing?.amount) - parseFloat(newTransaction?.amount)) < 0.01;
    const sameDescription = existing?.description?.toLowerCase()?.trim() === newTransaction?.description?.toLowerCase()?.trim();
    
    return sameAmount && sameDescription && timeDiff <= oneHourMs;
  }) || null;
};

/**
 * Check for duplicate budget category name
 * @param {string} name - category name to check
 * @param {Array} existingCategories - array of existing categories
 * @param {string} excludeId - ID to exclude (for edit mode)
 * @returns {boolean} true if duplicate found
 */
export const checkDuplicateBudgetCategory = (name, existingCategories, excludeId = null) => {
  if (!name || !existingCategories?.length) return false;
  return existingCategories?.some(cat => 
    cat?.name?.toLowerCase()?.trim() === name?.toLowerCase()?.trim() &&
    cat?.id !== excludeId
  );
};

/**
 * Check for duplicate bill name
 * @param {string} billName - bill name to check
 * @param {Array} existingBills - array of existing bills
 * @param {string} excludeId - ID to exclude (for edit mode)
 * @returns {boolean} true if duplicate found
 */
export const checkDuplicateBill = (billName, existingBills, excludeId = null) => {
  if (!billName || !existingBills?.length) return false;
  return existingBills?.some(bill => 
    (bill?.bill_name || bill?.billName)?.toLowerCase()?.trim() === billName?.toLowerCase()?.trim() &&
    bill?.id !== excludeId
  );
};

/**
 * Check for duplicate goal title
 * @param {string} title - goal title to check
 * @param {Array} existingGoals - array of existing goals
 * @param {string} excludeId - ID to exclude (for edit mode)
 * @returns {boolean} true if duplicate found
 */
export const checkDuplicateGoal = (title, existingGoals, excludeId = null) => {
  if (!title || !existingGoals?.length) return false;
  return existingGoals?.some(goal => 
    goal?.title?.toLowerCase()?.trim() === title?.toLowerCase()?.trim() &&
    goal?.id !== excludeId
  );
};

/**
 * Check for duplicate investment ticker symbol
 * @param {string} symbol - ticker symbol to check
 * @param {Array} existingInvestments - array of existing investments
 * @param {string} excludeId - ID to exclude (for edit mode)
 * @returns {boolean} true if duplicate found
 */
export const checkDuplicateInvestment = (symbol, existingInvestments, excludeId = null) => {
  if (!symbol || !existingInvestments?.length) return false;
  return existingInvestments?.some(inv => 
    inv?.symbol?.toLowerCase()?.trim() === symbol?.toLowerCase()?.trim() &&
    inv?.id !== excludeId
  );
};

/**
 * Check for duplicate habit/system name
 * @param {string} name - habit name to check
 * @param {Array} existingSystems - array of existing systems
 * @param {string} excludeId - ID to exclude (for edit mode)
 * @returns {boolean} true if duplicate found
 */
export const checkDuplicateHabit = (name, existingSystems, excludeId = null) => {
  if (!name || !existingSystems?.length) return false;
  return existingSystems?.some(sys => 
    sys?.name?.toLowerCase()?.trim() === name?.toLowerCase()?.trim() &&
    sys?.id !== excludeId
  );
};

// Comprehensive form validator
export const validateForm = (formData, validationRules) => {
  const errors = {};
  
  Object.keys(validationRules)?.forEach(fieldName => {
    const rules = validationRules?.[fieldName];
    const value = formData?.[fieldName];
    
    for (const rule of rules) {
      const error = rule(value, fieldName);
      if (error) {
        errors[fieldName] = error;
        break; // Stop at first error for this field
      }
    }
  });
  
  return {
    isValid: Object.keys(errors)?.length === 0,
    errors
  };
};

// Real-time field validator
export const validateField = (value, rules, fieldName) => {
  for (const rule of rules) {
    const error = rule(value, fieldName);
    if (error) return error;
  }
  return null;
};
